import { useEffect, useRef, useState } from 'react';
import { AuthContext } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { setupInterceptors } from '../services/axiosInstance';
import { logoutRequest, refreshToken } from '../services/api';

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const navigate = useNavigate();
  // accessTokenの最新値を参照するためのref
  const accessTokenRef = useRef(accessToken);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const updateAccessToken = (token: string | null) => {
    setAccessToken(token);
    accessTokenRef.current = token; // 即座に更新
  };

  const login = (token: string, userRole: string) => {
    setIsAuthenticated(true);
    updateAccessToken(token);
    setRole(userRole);
  };

  const logout = async () => {
    setIsAuthenticated(false);
    updateAccessToken(null);
    setRole(null);
    navigate('/login');
  };

  const logoutWithServerCall = async () => {
    try {
      await logoutRequest();
    } catch (error) {
      console.error('Logout API failed:', error);
    }
    logout();
  };

  // リロード対策
  const autoLogin = async () => {
    try {
      const response = await refreshToken();
      login(response.accessToken, response.role);
    } catch (error) {
      logout();
    }
  };

  // 初回ロード時にaxiosのインターセプター設定と自動ログイン試行
  useEffect(() => {
    setupInterceptors(
      () => accessTokenRef.current,
      updateAccessToken,
      logoutWithServerCall
    );
    autoLogin();
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, accessToken, role, login, logout }}
    >
      {isAuthenticated && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
